import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { btnGhost, btnPrimary } from '../ui';

const OLD_PATH = 'M10 50 C 80 10, 120 70, 160 40 S 240 20, 310 55';
const NEW_PATH = 'M10 50 C 70 55, 110 30, 160 35 S 250 60, 310 40';

const ExceptionDemo: React.FC = () => {
  const { t } = useTranslation();
  const logs = t('demo.ex.logs', { returnObjects: true }) as string[];
  const [step, setStep] = useState(0);
  const resolved = step >= 3;
  const visible = Array.isArray(logs) ? logs.slice(0, Math.min(logs.length, step + 1)) : [];

  return (
    <article className="rounded-2xl border border-line bg-graphite p-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[0.68rem] uppercase tracking-wide text-stamp">{t('demo.ex.label')}</p>
        <span className="rounded-full border border-mint/30 px-2 py-0.5 font-mono text-[0.65rem] text-mint">{t('demo.ex.badge')}</span>
      </header>
      <h3 className="mt-3 font-display text-2xl text-paper">{t('demo.ex.title')}</h3>
      <p className="mt-2 text-sm text-muted">{t('demo.ex.desc')}</p>
      <div className="mt-4 flex items-center gap-3">
        <span className={`rounded-full px-2 py-0.5 font-mono text-[0.65rem] ${resolved ? 'bg-mint-dim text-mint' : 'bg-coral-dim text-coral'}`}>
          {resolved ? t('demo.ex.resolved') : t('demo.ex.open')}
        </span>
        <span className="font-mono text-xs text-paper">{resolved ? t('demo.ex.lineResolved') : t('demo.ex.lineOpen')}</span>
      </div>
      <svg viewBox="0 0 320 80" className="mt-3 h-20 w-full" aria-hidden="true">
        <path d={OLD_PATH} fill="none" stroke="#ff4d6a" strokeWidth="2.5" opacity={resolved ? 0.3 : 1} />
        <path d={NEW_PATH} fill="none" stroke="#3dffc5" strokeWidth="2.5" opacity={resolved ? 1 : 0.2} />
      </svg>
      <div className="mt-2 min-h-[7.5rem] rounded-xl bg-ink p-3 font-mono text-[0.72rem] leading-relaxed text-stamp" aria-live="polite">
        {visible.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className={btnPrimary} onClick={() => setStep((value) => Math.min((Array.isArray(logs) ? logs.length : 1) - 1, value + 1))}>
          {t('demo.ex.approve')}
        </button>
        <button type="button" className={btnGhost} onClick={() => setStep(0)}>
          {t('demo.ex.reset')}
        </button>
      </div>
    </article>
  );
};

const FactoryScrub: React.FC = () => {
  const { t } = useTranslation();
  const rows = t('demo.fac.rows', { returnObjects: true }) as string[];
  const states = t('demo.fac.states', { returnObjects: true }) as string[];
  const [value, setValue] = useState(0);

  const label = useMemo(() => {
    if (!Array.isArray(states) || states.length === 0) return '';
    const index = Math.min(states.length - 1, Math.floor((value / 100) * states.length));
    return states[index];
  }, [states, value]);

  const statusFor = (index: number) => {
    const marks = [15, 40, 65, 90];
    const at = marks[index];
    if (value < at - 18) return t('demo.fac.dash');
    if (index === 0) return value >= at ? t('demo.fac.posted') : t('demo.fac.queued');
    if (index === 1) return value >= at ? t('demo.fac.posted') : t('demo.fac.syncing');
    if (index === 2) return value >= at ? t('demo.fac.packed') : t('demo.fac.picking');
    return value >= at ? t('demo.fac.issued') : t('demo.fac.draft');
  };

  return (
    <article className="rounded-2xl border border-line bg-graphite p-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[0.68rem] uppercase tracking-wide text-stamp">{t('demo.fac.label')}</p>
        <span className="rounded-full border border-amber/30 px-2 py-0.5 font-mono text-[0.65rem] text-amber">{t('demo.fac.badge')}</span>
      </header>
      <h3 className="mt-3 font-display text-2xl text-paper">{t('demo.fac.title')}</h3>
      <p className="mt-2 text-sm text-muted">{t('demo.fac.desc')}</p>
      <label className="mt-4 block text-sm text-muted" htmlFor="day-scrub">
        <span className="flex items-center justify-between">
          {t('demo.fac.progress')}
          <span className="font-mono text-xs text-mint">{label}</span>
        </span>
        <input
          id="day-scrub"
          className="mt-3 w-full accent-amber"
          type="range"
          min={0}
          max={100}
          value={value}
          onChange={(event) => setValue(Number(event.target.value))}
        />
      </label>
      <ul className="mt-4 space-y-2">
        {(Array.isArray(rows) ? rows : []).map((row, index) => (
          <li key={row} className="flex items-center justify-between rounded-lg border border-line bg-ink px-3 py-2 text-sm">
            <span>{row}</span>
            <em className="font-mono text-xs not-italic text-stamp">{statusFor(index)}</em>
          </li>
        ))}
      </ul>
    </article>
  );
};

const ProofDemos: React.FC = () => (
  <div className="grid gap-4 lg:grid-cols-2">
    <ExceptionDemo />
    <FactoryScrub />
  </div>
);

export default ProofDemos;
